import { Component, EventEmitter, Output, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { PackageFilterModalComponent } from '../package-filter-modal/package-filter-modal.component';
import { FilterOptions, SortOption } from '../../shared/types';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-package-filters',
  standalone: true,
  imports: [CommonModule, FormsModule, PackageFilterModalComponent],
  templateUrl: './package-filters.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./package-filters.component.scss'],
})
export class PackageFiltersComponent implements OnDestroy {
  @Output() viewChanged = new EventEmitter<string>();
  @Output() filtersApplied = new EventEmitter<FilterOptions>();
  @Output() filtersCleared = new EventEmitter<void>();
  @Output() tableSorted = new EventEmitter<SortOption>();
  @Output() searchChanged = new EventEmitter<string>();
  /** Opens the same Sort / drag-order modal as “Configure Sales Panel” on the package page. */
  @Output() openConfigureSortPanel = new EventEmitter<void>();

  searchText = '';
  selectedStatus = 'all';
  selectedType = 'all';
  activeView: 'grid' | 'list' = 'list';

  // Filter modal state
  isFilterModalVisible = false;

  // Track current active filters
  currentFilters: FilterOptions = {
    carWashTypes: [],
    statuses: [],
    locations: [],
    sortBy: '',
  };

  // Search debouncing
  private searchSubject = new Subject<string>();
  private destroy$ = new Subject<void>();

  constructor() {
    // Setup search debouncing
    this.searchSubject
      .pipe(
        debounceTime(300), // Wait 300ms after user stops typing
        distinctUntilChanged(), // Only emit if the value has changed
        takeUntil(this.destroy$) // Prevent memory leaks
      )
      .subscribe(searchTerm => {
        this.searchChanged.emit(searchTerm);
      });
  }

  setActiveView(view: 'grid' | 'list') {
    this.activeView = view;
    this.viewChanged.emit(this.activeView);
  }

  onSearchInput(event: any) {
    const searchTerm = event.target.value.trim();
    this.searchText = searchTerm;
    this.searchSubject.next(searchTerm);
  }

  clearSearch() {
    this.searchText = '';
    this.searchSubject.next('');
  }

  showFilterModal() {
    this.isFilterModalVisible = true;
  }

  closeFilterModal() {
    this.isFilterModalVisible = false;
  }

  onFiltersApplied(filters: FilterOptions) {
    this.currentFilters = { ...filters }; // Store the current filters
    this.filtersApplied.emit(filters);
    this.closeFilterModal();
  }

  /**
   * Clearing resets what the popup is holding, nothing else: the popup stays open and
   * currentFilters keeps describing what the table is actually showing, so the filter count
   * beside the button cannot claim nothing is applied while filtered rows are on screen. The
   * selection reaches the table through Apply (CWS-509).
   */
  onFiltersCleared() {
    this.filtersCleared.emit();
  }

  // Calculate the number of active filters
  getActiveFilterCount(): number {
    let count = 0;

    if (this.currentFilters.carWashTypes && this.currentFilters.carWashTypes.length > 0) {
      count += this.currentFilters.carWashTypes.length;
    }

    if (this.currentFilters.statuses && this.currentFilters.statuses.length > 0) {
      count += this.currentFilters.statuses.length;
    }

    if (this.currentFilters.locations && this.currentFilters.locations.length > 0) {
      count += this.currentFilters.locations.length;
    }

    if (this.currentFilters.sortBy && this.currentFilters.sortBy !== '') {
      count += 1;
    }

    return count;
  }

  // Check if any filters are active
  hasActiveFilters(): boolean {
    return this.getActiveFilterCount() > 0;
  }

  onSortTable(sortOption: SortOption) {
    this.tableSorted.emit(sortOption);
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
